/**
 * Phase 4b: Audit Log Page
 *
 * Read-only admin page for viewing the immutable action log.
 * Requires ACTION_LOG_VIEW permission.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';
import { hasPermission, PERMISSIONS } from '../authUtils';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useBackNavigation, createBackButton } from '../hooks/useBackNavigation';
import ErrorMessage from './ErrorMessage';
import '../styles/AuditLog.css';

export default function AuditLog() {
  const { setHeaderConfig, resetHeaderConfig } = usePageHeader();
  const handleBack = useBackNavigation('list', '/dashboard');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [resourceType, setResourceType] = useState('');
  const [actionType, setActionType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [limit, setLimit] = useState(50);

  // Permission check on mount
  const loadLogs = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (resourceType) params.append('resource_type', resourceType);
      if (actionType) params.append('action_type', actionType);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      params.append('limit', limit);

      const response = await api.get('/audit-logs?' + params.toString());
      setLogs(response.data.logs || []);
    } catch (err) {
      setError('Failed to load audit logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [resourceType, actionType, fromDate, toDate, limit]);

  useEffect(() => {
    if (!hasPermission(PERMISSIONS.ACTION_LOG_VIEW)) {
      setError('You do not have permission to view the audit log.');
      setLoading(false);
      return;
    }

    loadLogs();
  }, [loadLogs]);

  // Set up unified header
  useEffect(() => {
    setHeaderConfig({
      backButton: createBackButton(handleBack),
      title: 'Audit Log',
      subtitle: 'View immutable operational audit trail',
      actions: [],
    });

    // Cleanup: reset header when unmounting
    return () => resetHeaderConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderConfig, resetHeaderConfig]);

  const handleFilterChange = () => {
    // Reset offset on filter change
    loadLogs();
  };

  const handleResetFilters = () => {
    setResourceType('');
    setActionType('');
    setFromDate('');
    setToDate('');
    setLimit(50);
    loadLogs();
  };

  const formatTimestamp = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  if (!hasPermission(PERMISSIONS.ACTION_LOG_VIEW)) {
    return (
      <div className="audit-log-container">
        <h1>Audit Log</h1>
        <ErrorMessage message="You do not have permission to view the audit log." />
      </div>
    );
  }

  return (
    <div className="audit-log-container">
      <h1>Audit Log</h1>
      <p className="subtitle">View immutable operational audit trail of all CRM actions</p>

      {error && <ErrorMessage message={error} />}

      {/* Filters */}
      <div className="audit-filters">
        <div className="filter-group">
          <label>Resource Type:</label>
          <select
            value={resourceType}
            onChange={(e) => setResourceType(e.target.value)}
          >
            <option value="">All</option>
            <option value="merchant">Merchant</option>
            <option value="merchant_address">Merchant Address</option>
            <option value="owner">Owner</option>
            <option value="bank_account">Bank Account</option>
            <option value="additional_entity">Additional Entity</option>
            <option value="deal">Deal</option>
            <option value="funding_entity">Funding Entity</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Action Type:</label>
          <input
            type="text"
            placeholder="e.g., merchant.created"
            value={actionType}
            onChange={(e) => setActionType(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>From Date:</label>
          <input
            type="datetime-local"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>To Date:</label>
          <input
            type="datetime-local"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Limit:</label>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>

        <button className="btn-primary" onClick={handleFilterChange}>
          Apply Filters
        </button>
        <button className="btn-secondary" onClick={handleResetFilters}>
          Reset
        </button>
      </div>

      {/* Loading state */}
      {loading && <p>Loading audit logs...</p>}

      {/* Table */}
      {!loading && logs.length > 0 && (
        <div className="audit-table-wrapper">
          <table className="audit-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Resource</th>
                <th>Resource Label</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td>{formatTimestamp(log.created_at)}</td>
                  <td>
                    <span className="actor-email">{log.actor_email || `User ${log.actor_user_id}`}</span>
                  </td>
                  <td>
                    <span className={`action-badge action-${log.action_type.split('.')[0]}`}>
                      {log.action_type}
                    </span>
                  </td>
                  <td>{log.resource_type}</td>
                  <td>{log.resource_label || `ID: ${log.resource_id}`}</td>
                  <td>
                    {log.new_state_json && (
                      <details className="log-details">
                        <summary>View</summary>
                        <pre>{JSON.stringify(log.new_state_json, null, 2)}</pre>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && logs.length === 0 && !error && (
        <p className="no-results">No audit logs found matching the filter criteria.</p>
      )}
    </div>
  );
}
